import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Shield, Ticket, Clock, CheckCircle2, AlertTriangle,
  PlusCircle, ArrowRight, RefreshCw, Search, TrendingUp,
  FileText, LogOut, Download, MessageCircle, Send, X, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  assigned: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [audits, setAudits] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('tickets');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId] = useState(() => `kb-${Date.now()}`);

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const q = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setChatLoading(true);
    try {
      const { data } = await api.post('/api/enhance/kb-chat', { question: q, session_id: chatSessionId });
      setChatMessages(prev => [...prev, { role: 'assistant', text: data.answer }]);
    } catch { setChatMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I could not process your question. Please try again.' }]); }
    finally { setChatLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [ticketsRes, auditsRes] = await Promise.all([
          api.get('/api/tickets?limit=50'),
          api.get('/api/growth/audits'),
        ]);
        setTickets(ticketsRes.data.tickets || []);
        setAudits(auditsRes.data.audits || []);
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, []);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const filteredTickets = tickets.filter(t =>
    !search || t.title?.toLowerCase().includes(search.toLowerCase()) || t.status?.includes(search.toLowerCase())
  );

  const openCount = tickets.filter(t => ['new', 'assigned', 'in_progress'].includes(t.status)).length;
  const resolvedCount = tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-portal">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-base font-bold text-gray-900 tracking-tight">TechOps Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{user?.name}</span>
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => navigate('/dashboard')} data-testid="portal-dashboard-btn">Full Dashboard</Button>
            <button onClick={logout} className="text-gray-400 hover:text-red-500 transition-colors" data-testid="portal-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="space-y-6 animate-fade-in">
          {/* Welcome */}
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Welcome, {user?.name}</h1>
            <p className="text-sm text-gray-500 mt-1">Track your tickets, view audits, and manage your account</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <Ticket className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="font-heading text-2xl font-bold text-gray-900">{tickets.length}</p>
              <p className="text-xs text-gray-500">Total Tickets</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
              <Clock className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-amber-700">{openCount}</p>
              <p className="text-xs text-amber-600">Open</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-emerald-700">{resolvedCount}</p>
              <p className="text-xs text-emerald-600">Resolved</p>
            </div>
            <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5">
              <TrendingUp className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="font-heading text-2xl font-bold text-[#D4A843]">{audits.length}</p>
              <p className="text-xs text-[#D4A843]">Audits</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[{k: 'tickets', l: 'My Tickets'}, {k: 'audits', l: 'Audit Reports'}].map(tab => (
              <button key={tab.k} onClick={() => setView(tab.k)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === tab.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`portal-tab-${tab.k}`}
              >{tab.l}</button>
            ))}
          </div>

          {/* Tickets View */}
          {view === 'tickets' && (
            <div className="space-y-4" data-testid="portal-tickets">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tickets..." className="rounded-lg pl-10" data-testid="portal-search" />
                </div>
                <Button onClick={() => navigate('/tickets/new')} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="portal-new-ticket">
                  <PlusCircle className="w-4 h-4 mr-2" /> New Ticket
                </Button>
                <a href={`${API_URL}/api/export/tickets`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="text-xs rounded-lg" data-testid="portal-export-tickets">
                    <Download className="w-3 h-3 mr-1" /> CSV
                  </Button>
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="portal-ticket-table">
                  <thead><tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ticket</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">SLA</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTickets.map(t => {
                      const sla = t.sla;
                      let slaLabel = '—';
                      if (sla?.resolution_deadline) {
                        const deadline = new Date(sla.resolution_deadline);
                        const now = new Date();
                        const remaining = (deadline - now) / 60000;
                        if (t.status === 'resolved' || t.status === 'closed') slaLabel = 'Met';
                        else if (remaining <= 0) slaLabel = 'Breached';
                        else if (remaining < 60) slaLabel = `${Math.round(remaining)}m left`;
                        else slaLabel = `${Math.round(remaining / 60)}h left`;
                      }
                      return (
                        <tr key={t.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)} data-testid={`portal-ticket-${t.id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 truncate max-w-[250px]">{t.title}</p>
                            <p className="text-[10px] text-gray-400 font-mono">{t.id?.slice(0, 8)}</p>
                          </td>
                          <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</span></td>
                          <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</span></td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{t.service_type || t.category || '—'}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3"><span className={`text-xs font-semibold ${slaLabel === 'Breached' ? 'text-red-600' : slaLabel === 'Met' ? 'text-emerald-600' : 'text-gray-600'}`}>{slaLabel}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredTickets.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No tickets found</p>}
              </div>
            </div>
          )}

          {/* Audits View */}
          {view === 'audits' && (
            <div className="space-y-4" data-testid="portal-audits">
              <div className="flex justify-end">
                <a href={`${API_URL}/api/export/audits`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="text-xs rounded-lg" data-testid="portal-export-audits">
                    <Download className="w-3 h-3 mr-1" /> Export CSV
                  </Button>
                </a>
              </div>
              {audits.length === 0 ? (
                <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">No audit reports yet</p>
                  <Button onClick={() => navigate('/growth')} variant="outline" className="mt-3 text-xs rounded-lg" data-testid="portal-go-audit">Run Your First Audit</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {audits.map(a => {
                    const score = a.analysis?.overall_score || 0;
                    const color = score >= 75 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
                    return (
                      <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`portal-audit-${a.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">{a.business_info?.name}</h4>
                            <p className="text-xs text-gray-400">{a.business_info?.industry} | {new Date(a.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-center">
                            <p className={`font-heading text-2xl font-bold ${color}`}>{score}</p>
                            <p className="text-[10px] text-gray-400">Score</p>
                          </div>
                        </div>
                        {a.analysis?.summary && <p className="text-xs text-gray-500 mb-3">{a.analysis.summary}</p>}
                        {a.analysis?.quick_wins?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Quick Wins</p>
                            <ul className="space-y-1">{a.analysis.quick_wins.slice(0, 3).map((w, i) => (
                              <li key={`item-${i}`} className="text-xs text-gray-600 flex items-start gap-1.5">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />{w}
                              </li>
                            ))}</ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* KB Chatbot Widget */}
      {chatOpen && (
        <div className="fixed bottom-20 right-6 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col" style={{ height: '400px' }} data-testid="kb-chatbot-panel">
          <div className="bg-[#D4A843] text-[#0A0A0A] font-semibold px-4 py-3 rounded-t-lg flex items-center justify-between">
            <p className="text-sm font-bold">TechOps Assistant</p>
            <button onClick={() => setChatOpen(false)} data-testid="kb-chat-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages.length === 0 && <p className="text-xs text-gray-400 text-center py-8">Ask me anything about our services!</p>}
            {chatMessages.map((m, i) => (
              <div key={`item-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-xs ${m.role === 'user' ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-800'}`} data-testid={`kb-chat-msg-${i}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {chatLoading && <div className="flex justify-start"><div className="px-3 py-2 rounded-lg bg-gray-100"><Loader2 className="w-3 h-3 animate-spin text-gray-400" /></div></div>}
          </div>
          <div className="border-t border-gray-200 p-2 flex gap-2">
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendChat()}
              placeholder="Ask a question..."
              className="flex-1 text-xs px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4A843]"
              data-testid="kb-chat-input"
            />
            <button onClick={sendChat} className="bg-[#D4A843] text-[#0A0A0A] font-semibold p-2 rounded-lg" data-testid="kb-chat-send"><Send className="w-3 h-3" /></button>
          </div>
        </div>
      )}
      <button onClick={() => setChatOpen(!chatOpen)} className="fixed bottom-6 right-6 w-12 h-12 bg-[#D4A843] text-[#0A0A0A] font-semibold rounded-full flex items-center justify-center shadow-lg hover:bg-[#E8C972] z-50" data-testid="kb-chat-toggle">
        <MessageCircle className="w-5 h-5" />
      </button>
    </div>
  );
}
